import { useState } from 'react';
import api from '../../../../../api';

export const useTaskForm = (lead) => {
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    dueDate: '',
    priority: '',
    status: '',
    description: '',
    assignedUser: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);

  const validateForm = () => {
    const errors = {};
    if (!formData.title) errors.title = 'Task title is required';
    if (!formData.type) errors.type = 'Task type is required';
    if (!formData.dueDate) errors.dueDate = 'Due date is required';
    if (!formData.priority) errors.priority = 'Priority is required';
    if (!formData.status) errors.status = 'Status is required';
    return errors;
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return false;
    }

    try {
      // Get assigned user ID from lead
      let assignedUserId = null;
      
      console.log('=== Task Submit Debug - START ===');
      console.log('Full lead object:', lead);
      console.log('lead.assignedUserId:', lead?.assignedUserId);
      console.log('lead.assignedTo:', lead?.assignedTo);
      console.log('lead.assignedTo?.id:', lead?.assignedTo?.id);
      console.log('lead.assignUser:', lead?.assignUser);
      console.log('formData.assignedUser:', formData.assignedUser);
      
      // Priority 1: Try formData.assignedUser (if manually set)
      if (formData.assignedUser) {
        const parsed = parseInt(formData.assignedUser);
        if (!isNaN(parsed)) {
          assignedUserId = parsed;
          console.log('✅ Using formData.assignedUser:', assignedUserId);
        }
      }
      
      // Priority 2: Try lead.assignedUserId (direct field from API)
      if (!assignedUserId && lead?.assignedUserId) {
        assignedUserId = lead.assignedUserId;
        console.log('✅ Using lead.assignedUserId:', assignedUserId);
      }
      
      // Priority 3: Try lead.assignedTo.id (from nested object)
      if (!assignedUserId && lead?.assignedTo?.id) {
        assignedUserId = lead.assignedTo.id;
        console.log('✅ Using lead.assignedTo.id:', assignedUserId);
      }
      
      // Priority 4: Try lead.assignUser (parse if numeric)
      if (!assignedUserId && lead?.assignUser) {
        const parsed = parseInt(lead.assignUser);
        if (!isNaN(parsed)) {
          assignedUserId = parsed;
          console.log('✅ Using lead.assignUser (parsed):', assignedUserId);
        } else {
          console.warn('⚠️ lead.assignUser is not numeric:', lead.assignUser);
        }
      }
      
      if (!assignedUserId) {
        console.error('❌ Could not determine assigned user ID from any source!');
      }
      
      console.log('Final assignedUserId to be sent:', assignedUserId);
      console.log('=== Task Submit Debug - END ===');
      
      // Get companyId from lead (similar to how Task page does it)
      let companyId = null;
      if (lead?.companyId) {
        companyId = typeof lead.companyId === 'number' ? lead.companyId : parseInt(lead.companyId);
        if (isNaN(companyId)) companyId = null;
      } else if (lead?.company) {
        const parsed = parseInt(lead.company);
        if (!isNaN(parsed)) companyId = parsed;
      }
      
      // Get contactId from lead (similar to how Task page does it)
      let contactId = null;
      if (lead?.contactId) {
        contactId = typeof lead.contactId === 'number' ? lead.contactId : parseInt(lead.contactId);
        if (isNaN(contactId)) contactId = null;
      } else if (lead?.contact) {
        const parsed = parseInt(lead.contact);
        if (!isNaN(parsed)) contactId = parsed;
      } else if (lead?.pic) {
        const parsed = parseInt(lead.pic);
        if (!isNaN(parsed)) contactId = parsed;
      }
      
      console.log('Company ID from lead:', companyId);
      console.log('Contact ID from lead:', contactId);
      
      const taskData = {
        ...formData,
        leadId: lead.id,
        dueDate: new Date(formData.dueDate).toISOString(),
        assignedUserId: assignedUserId,
        companyId: companyId, // Add companyId for logging
        contactId: contactId   // Add contactId for logging
      };
      
      console.log('Task data being sent:', taskData);
      
      const form = new FormData();
      form.append("data", new Blob([JSON.stringify(taskData)], { type: "application/json" }));

      // Add file if uploaded
      if (uploadedFiles.length > 0) {
        form.append("file", uploadedFiles[0]);
      }

      if (isEditMode && editingTaskId) {
        // Update existing task
        await api.put(`/crm/lead-tasks/${editingTaskId}`, form, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      } else {
        // Create new task
        await api.post("/crm/lead-tasks", form, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      }

      resetForm();
      return true;
    } catch (error) {
      console.error("Error saving task:", error);
      return false;
    }
  };

  const onDrop = (acceptedFiles) => {
    setUploadedFiles(acceptedFiles);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: '',
      dueDate: '',
      priority: '',
      status: '',
      description: '',
      assignedUser: ''
    });
    setFormErrors({});
    setUploadedFiles([]);
    setIsEditMode(false);
    setEditingTaskId(null);
  };

  const populateFormForEdit = (task) => {
    setFormData({
      title: task.title || '',
      type: task.type || '',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
      priority: task.priority || '',
      status: task.status || '',
      description: task.description || '',
      assignedUser: task.assignedUser || task.assignedUserId || ''
    });
    setUploadedFiles([]); // Reset files for edit mode
    setIsEditMode(true);
    setEditingTaskId(task.id);
  };

  return {
    formData,
    formErrors,
    uploadedFiles,
    isEditMode,
    editingTaskId,
    handleInputChange,
    handleSubmit,
    onDrop,
    removeFile,
    resetForm,
    populateFormForEdit
  };
}; 